import React, { useState } from 'react';
import { CheckIcon, XIcon, StarIcon, LockIcon, ShieldIcon, ZapIcon, SmartphoneIcon, GiftIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, MailIcon, PawPrintIcon } from 'lucide-react';
import { motion } from 'framer-motion';
const CHECKOUT_URL = 'https://afrekenen.stresslessdogs.nl/checkout/liefdevol-communiceren-g';
const CTAButton = ({
  label = 'Ja, ik wil Liefdevol Communiceren - voor €127 →',
  className = ''
}: {
  label?: string;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`block w-full md:w-auto md:inline-block bg-[#ffb800] hover:bg-[#e6a600] text-[#1a2b4a] font-bold py-4 px-8 rounded-xl text-center transition-colors shadow-lg text-lg ${className}`} data-id="element-996">
    {label}
  </a>;
const TrustIcons = () => <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm text-gray-500 mt-4" data-id="element-997">
    <span className="flex items-center gap-1.5" data-id="element-998">
      <LockIcon className="w-4 h-4" data-id="element-999" /> Veilig betalen
    </span>
    <span className="flex items-center gap-1.5" data-id="element-1000">
      <ShieldIcon className="w-4 h-4" data-id="element-1001" /> 30 dagen garantie
    </span>
    <span className="flex items-center gap-1.5" data-id="element-1002">
      <ZapIcon className="w-4 h-4" data-id="element-1003" /> Direct toegang
    </span>
    <span className="flex items-center gap-1.5" data-id="element-1004">
      <SmartphoneIcon className="w-4 h-4" data-id="element-1005" /> Werkt op alle apparaten
    </span>
  </div>;
const Stars = ({
  count = 5
}: {
  count?: number;
}) => <div className="flex gap-0.5" data-id="element-1006">
    {Array.from({
    length: count
  }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-1007" />)}
  </div>;
const TestimonialCard = ({
  quote,
  name,
  stars = 5
}: {
  quote: string;
  name: string;
  stars?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5
}} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-full" data-id="element-1008">
    <Stars count={stars} data-id="element-1009" />
    <p className="text-gray-700 italic mt-4 mb-4 flex-1 leading-relaxed" data-id="element-1010">
      "{quote}"
    </p>
    <p className="text-sm font-bold text-[#1a2b4a]" data-id="element-1011">- {name}</p>
  </motion.div>;
const PriceBlock = () => <div className="text-center mb-6" data-id="element-1012">
    <p className="text-gray-500 text-lg line-through mb-1" data-id="element-1013">
      Totale waarde: €223,90
    </p>
    <p className="text-4xl md:text-5xl font-black text-[#1a2b4a]" data-id="element-1014">
      Nu slechts €127
    </p>
    <p className="text-gray-600 mt-1" data-id="element-1015">Eenmalige betaling</p>
  </div>;
const FAQItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-gray-200" data-id="element-1016">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left" data-id="element-1017">
        <span className="font-bold text-[#1a2b4a] text-lg pr-4" data-id="element-1018">{q}</span>
        {open ? <ChevronUpIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-1019" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-1020" />}
      </button>
      {open && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="pb-5 text-gray-600 leading-relaxed" data-id="element-1021">
          {a}
        </motion.div>}
    </div>;
};
export function LiefdevolCommunicerenCursusPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1022">
      {/* SECTION 1: STICKY TOP BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#e6f7f5] border-t border-[#5ce0d2]/30 py-2.5 px-4" data-id="element-1023">
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 text-sm md:text-base" data-id="element-1024">
          <span className="text-[#1a2b4a]" data-id="element-1025">
            🐾 Tijdelijke aanbieding:{' '}
            <span className="line-through" data-id="element-1026">€223,90</span> -{' '}
            <strong data-id="element-1027">Nu slechts €127</strong>
          </span>
          <a href="#bestellen" className="text-[#1a2b4a] font-bold underline hover:text-[#5ce0d2] transition-colors ml-2 whitespace-nowrap" data-id="element-1028">
            Ja, ik wil beginnen →
          </a>
        </div>
      </div>

      {/* SECTION 2: HEADER */}
      <div className="pt-12" data-id="element-1029">
        <nav className="border-b border-gray-100 py-4 px-4 bg-white" data-id="element-1030">
          <div className="flex justify-center md:justify-between items-center max-w-6xl mx-auto" data-id="element-1031">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6YKzftRpGkz4jKTtn8FQxB/Logo_Stressless_Dogs_%288%29_%284%29.png" alt="Stressless Dogs" className="h-10 object-contain" data-id="element-1032" />
            <a href="mailto:amee@stresslessdogs.nl" className="hidden md:flex items-center text-sm font-medium text-gray-500 hover:text-[#5ce0d2] transition-colors" data-id="element-1033">
              <MailIcon className="w-4 h-4 mr-2" data-id="element-1034" />
              Hulp nodig? Mail mij op amee@stresslessdogs.nl
            </a>
          </div>
        </nav>
      </div>

      {/* SECTION 2: HERO */}
      <section className="max-w-6xl mx-auto px-5 py-12 md:py-20" data-id="element-1035">
        <div className="grid md:grid-cols-12 gap-10 items-center" data-id="element-1036">
          <div className="md:col-span-7 order-2 md:order-1" data-id="element-1037">
            <p className="text-sm italic text-[#5ce0d2] mb-3" data-id="element-1038">
              Speciaal voor liefdevolle hondeneigenaren:
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1a2b4a] mb-5 leading-tight" data-id="element-1039">
              Je hond wil wél luisteren.
              <br data-id="element-1040" />
              Hij begrijpt je gewoon niet.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1041">
              Ontdek de verborgen taalbarrière die voorkomt dat jullie écht op
              één lijn zitten - en hoe je die doorbreekt zonder te straffen, te
              commanderen of te domineren.
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8" data-id="element-1042">
              <span className="flex items-center gap-1" data-id="element-1043">
                <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-1044" />
                <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-1045" />
                <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-1046" />
                <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-1047" />{' '}
                4,3 op Trustpilot
              </span>
              <span data-id="element-1048">🐾 15.000+ honden geholpen</span>
              <span className="flex items-center gap-1" data-id="element-1049">
                <CheckIcon className="w-4 h-4 text-green-500" data-id="element-1050" /> 1054+
                beoordelingen
              </span>
            </div>
            <CTAButton label="Ja, ik wil de Hondentaal leren spreken - voor slechts €127 →" data-id="element-1051" />
            <p className="text-xs text-gray-400 mt-3" data-id="element-1052">
              30 dagen niet-goed-geld-terug garantie · Direct toegang ·
              Eenmalige betaling
            </p>
          </div>
          <div className="md:col-span-5 order-1 md:order-2" data-id="element-1053">
            <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1054">
              <img src="https://cdn.magicpatterns.com/uploads/vZLarCb4ynRSjgCisN7z8e/freepik_middelgrote-hond-in-een-g_2777443833.png" alt="Vrouw met hond in een ontspannen setting" className="w-full h-auto object-cover" data-id="element-1055" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: EMPATHY & RECOGNITION */}
      <section className="bg-[#faf5eb] py-16 md:py-20 px-5" data-id="element-1056">
        <div className="max-w-2xl mx-auto text-lg text-gray-700 leading-relaxed space-y-5" data-id="element-1057">
          <p className="text-2xl font-bold text-[#1a2b4a]" data-id="element-1058">
            Mag ik je iets eerlijks vragen?
          </p>
          <p data-id="element-1059">
            Je houdt van je hond. Echt. Je geeft hem de beste voeding, lange
            wandelingen, een warme plek op de bank. Je hebt misschien al van
            alles geprobeerd - cursussen, tips van trainers, YouTube-video's.
          </p>
          <p data-id="element-1060">En toch…</p>
          <p data-id="element-1061">
            Toch is er die wandeling waarbij je hart een beetje sneller klopt
            als je een andere hond ziet aankomen.
          </p>
          <p data-id="element-1062">
            Toch is er dat moment als de deurbel gaat en je al weet wat er gaat
            gebeuren.
          </p>
          <p data-id="element-1063">
            Toch is er dat gevoel van:{' '}
            <em data-id="element-1064">"Waarom luistert hij nou niet? Ik doe toch alles goed?"</em>
          </p>
          <p data-id="element-1065">
            Dat gevoel kent bijna elke hondeneigenaar. En het heeft niets te
            maken met hoe goed jij je best doet.
          </p>
          <p className="font-bold text-[#1a2b4a]" data-id="element-1066">
            Het heeft alles te maken met communicatie.
          </p>
        </div>
      </section>

      {/* SECTION 4: THE PROBLEM */}
      <section className="py-16 md:py-20 px-5" data-id="element-1067">
        <div className="max-w-2xl mx-auto" data-id="element-1068">
          <h2 className="text-3xl font-bold text-[#5ce0d2] text-center mb-8" data-id="element-1069">
            Waarom de taalbarrière tussen jou en je hond alles verandert
          </h2>
          <div className="text-lg text-gray-700 leading-relaxed space-y-5 mb-10" data-id="element-1070">
            <p data-id="element-1071">
              Jij communiceert de hele dag met je hond. Met je stem, je woorden,
              je handen. Dat is hoe mensen van nature communiceren.
            </p>
            <p data-id="element-1072">
              Maar je hond communiceert via lichaamstaal, energie en beweging.
              Dat is zijn moedertaal. De taal die hij al zijn hele leven
              vloeiend spreekt.
            </p>
            <p data-id="element-1073">
              Het is alsof jij de hele dag in het Nederlands praat tegen iemand
              die alleen Spaans spreekt. Hij snapt de toon, hij voelt de emotie
              - maar de boodschap komt maar voor een deel aan.
            </p>
          </div>

          {/* Comparison Card */}
          <div className="grid md:grid-cols-2 gap-4 mb-10" data-id="element-1074">
            <div className="bg-[#faf5eb] rounded-2xl p-6 border border-gray-100" data-id="element-1075">
              <p className="font-bold text-[#1a2b4a] mb-4 text-lg" data-id="element-1076">
                🗣️ Hoe mensen communiceren
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-1077">
                <li data-id="element-1078">Stem en woorden</li>
                <li data-id="element-1079">Handen en gebaren</li>
                <li data-id="element-1080">Verbale instructies</li>
                <li data-id="element-1081">Emotionele toon</li>
              </ul>
            </div>
            <div className="bg-[#e6f7f5] rounded-2xl p-6 border border-[#5ce0d2]/20" data-id="element-1082">
              <p className="font-bold text-[#1a2b4a] mb-4 text-lg" data-id="element-1083">
                🐾 Hoe honden communiceren
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-1084">
                <li data-id="element-1085">Lichaamstaal</li>
                <li data-id="element-1086">Energie en beweging</li>
                <li data-id="element-1087">Subtiele signalen</li>
                <li data-id="element-1088">Lichaamshouding</li>
              </ul>
            </div>
          </div>

          <div className="text-lg text-gray-700 leading-relaxed space-y-5" data-id="element-1089">
            <p data-id="element-1090">
              Dit is <strong data-id="element-1091">Het Mensentaal Probleem</strong> - en het is de
              echte reden waarom commando's niet landen, waarom je hond soms
              doet alsof hij je niet hoort, en waarom die diepe verbinding die
              je voelt dat er moet zijn… er nog niet helemaal is.
            </p>
            <p data-id="element-1092">
              Dit heeft niets te maken met hoe slim je hond is. Niets met hoe
              hard je je best doet.{' '}
              <strong data-id="element-1093">Het heeft alles te maken met communicatie.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5: WHAT DOESN'T WORK */}
      <section className="bg-[#faf5eb] py-16 md:py-20 px-5" data-id="element-1094">
        <div className="max-w-2xl mx-auto" data-id="element-1095">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-10" data-id="element-1096">
            Waarom de meeste aanpakken niet werken
          </h2>
          <div className="space-y-5 mb-10" data-id="element-1097">
            {[{
            title: 'Streng zijn en "de baas spelen"',
            desc: 'De hond wordt er alleen maar nerveuzer van - en de band wordt slechter, niet beter.'
          }, {
            title: 'Commando\'s herhalen ("Zit! Zit! Zit!")',
            desc: 'Hij hoort je wel, maar begrijpt de boodschap niet volledig in jouw taal.'
          }, {
            title: 'Hondenschool met groepslessen',
            desc: 'In een drukke ruimte is je hond zo overprikkeld dat hij niets opneemt.'
          }, {
            title: 'Privétrainer die "dominantie" predikt',
            desc: 'Na de sessies gaat alles weer precies hetzelfde - omdat de communicatie niet veranderd is.'
          }, {
            title: "Tips van vrienden en YouTube-video's",
            desc: 'Wat bij hun hond werkt, werkt bij de jouwe niet - want elke hond en elk baasje is anders.'
          }].map((item, i) => <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start gap-4" data-id="element-1098">
                <XIcon className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1099" />
                <div data-id="element-1100">
                  <p className="font-bold text-[#1a2b4a] mb-1" data-id="element-1101">{item.title}</p>
                  <p className="text-gray-600" data-id="element-1102">{item.desc}</p>
                </div>
              </div>)}
          </div>
          <p className="text-xl font-bold text-[#1a2b4a] text-center leading-relaxed" data-id="element-1103">
            Al deze aanpakken proberen je hond jóuw taal te leren.
            <br data-id="element-1104" />
            Maar dat werkt niet. De oplossing is precies andersom.
          </p>
        </div>
      </section>

      {/* SECTION 6: AMEE'S STORY */}
      <section className="py-16 md:py-20 px-5" data-id="element-1105">
        <div className="max-w-6xl mx-auto" data-id="element-1106">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-12" data-id="element-1107">
            Hoe ik ontdekte dat alles wat ik geleerd had… niet klopte
          </h2>
          <div className="grid md:grid-cols-12 gap-10 items-start" data-id="element-1108">
            <div className="md:col-span-7 text-lg text-gray-700 leading-relaxed space-y-5" data-id="element-1109">
              <p data-id="element-1110">
                Mijn naam is Amee de Bruijn, en ik schaam me eerlijk gezegd een
                beetje als ik terugdenk aan hoe ik vroeger met honden omging.
              </p>
              <p data-id="element-1111">
                Ik studeerde voor hondengedragsconsultant. Ik leerde alles wat
                er te leren viel. En wat me geleerd werd, was: wees de baas.
                Wees dominant. Honden zijn afstammelingen van wolven, dus je
                moet de alfawolf zijn.
              </p>
              <p data-id="element-1112">
                Ik deed het. Ik probeerde het. En ik voelde me er vreselijk bij.
              </p>
              <p data-id="element-1113">
                Want ik hield van honden. Echt. En dan moest ik ze behandelen
                alsof het robots waren - alsof je op de juiste knopjes moest
                drukken en dan zouden ze gehoorzamen.
              </p>
              <p data-id="element-1114">Totdat ik iets ontdekte wat alles veranderde.</p>
              <p data-id="element-1115">
                Ik leerde dat honden in het wild helemaal geen duidelijke
                "alfaleider" hebben. Ze leven meer als een gezin. Ze
                communiceren. Ze houden rekening met elkaar. Ze lezen elkaars
                lichaamstaal met een precisie die wij mensen ons nauwelijks
                kunnen voorstellen.
              </p>
              <p data-id="element-1116">
                Mijn mentor in deze ontdekking was <strong data-id="element-1117">Turid Rugaas</strong>
                , een Noorse hondengedragsdeskundige die de wereld van
                hondencommunicatie letterlijk op zijn kop heeft gezet. Zij
                ontdekte de "calming signals" - de subtiele lichaamstaalsignalen
                waarmee honden de hele dag met elkaar communiceren.
              </p>
              <p data-id="element-1118">
                Toen ik haar werk leerde kennen, viel alles op zijn plek. Ik
                begon honden te benaderen in hun eigen taal. En wat er toen
                gebeurde… dat vergeet ik nooit meer.
              </p>
              <p data-id="element-1119">
                Honden die al jaren gedragsproblemen hadden - honden waar geen
                enkele trainer iets mee kon - die werden rustiger. Opener. Ze
                zochten toenadering in plaats van te vluchten.
              </p>
              <p data-id="element-1120">
                En de baasjes? Die zeiden steeds hetzelfde:{' '}
                <em data-id="element-1121">
                  "Ik begrijp mijn hond nu eindelijk. Ik kijk anders naar hem.
                  Ik voel dat we écht contact hebben."
                </em>
              </p>
            </div>
            <div className="md:col-span-5" data-id="element-1122">
              <div className="sticky top-24" data-id="element-1123">
                <img src="https://cdn.magicpatterns.com/uploads/vXmyJc5cCVvbLnBaXGCixn/682f298cbb713_AmeemetSophietje1.png" alt="Amee de Bruijn met haar hond" className="w-full rounded-2xl shadow-lg" data-id="element-1124" />
              </div>
            </div>
          </div>
          <div className="max-w-3xl mx-auto mt-12" data-id="element-1125">
            <blockquote className="text-2xl md:text-3xl font-bold text-[#5ce0d2] text-center italic leading-snug" data-id="element-1126">
              "Honden hebben geen baas nodig. Ze hebben een ouder nodig. Iemand
              die hun taal spreekt."
            </blockquote>
            <p className="text-center text-gray-500 mt-3" data-id="element-1127">- Amee de Bruijn</p>
          </div>
        </div>
      </section>

      {/* SECTION 7: FIRST TESTIMONIALS */}
      <section className="bg-[#faf5eb] py-16 md:py-20 px-5" data-id="element-1128">
        <div className="max-w-5xl mx-auto" data-id="element-1129">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-12" data-id="element-1130">
            Wat hondeneigenaren zeggen nadat ze de Hondentaal leerden spreken
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-1131">
            <TestimonialCard quote="De cursus liefdevol communiceren met je hond is echt een eyeopener en de communicatie lichaamstaal werkt super goed. Mijn hond begrijpt het meteen." name="Cursist Liefdevol Communiceren" data-id="element-1132" />
            <TestimonialCard quote="Ik kijk nu ook echt anders naar mijn hond. Waar ik dacht vooral een angstige hond te hebben, blijkt zij gewoon heel beleefd te zijn. Mijn benadering naar haar toe is daardoor anders." name="Cursist Liefdevol Communiceren" data-id="element-1133" />
            <TestimonialCard quote="Echt helemaal WOW!!! Ik was ten einde raad! Ik had al veel geprobeerd, veel raad gekregen en heel veel geld uitgegeven. Aïko is super blij met de 'taakjes' die hij nu krijgt en wij zijn blij dat we hem eindelijk rustig kregen!" name="Inge Vercammen" data-id="element-1134" />
            <TestimonialCard quote="Ook al dachten we al veel te weten na 12 jaar een Golden Retriever gehad te hebben, was het toch nog erg leerzaam. Je merkt nu pas hoeveel je uitgaat van het praten in plaats van lichaamstaal." name="Cursist met 12 jaar hondenervaring" data-id="element-1135" />
          </div>
        </div>
      </section>

      {/* SECTION 8: PRODUCT REVEAL */}
      <section className="py-16 md:py-20 px-5" data-id="element-1136">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1137">
          <h2 className="text-3xl md:text-4xl font-black text-[#1a2b4a] mb-6" data-id="element-1138">
            Introduceer: De Liefdevol Communiceren Cursus
          </h2>
          <div className="mb-8" data-id="element-1139">
            <img src="https://cdn.magicpatterns.com/uploads/d7mvv6jB1XyRHfPv1QLUbd/Verlatingangst_kernmethode_bonus_(2).png" alt="Liefdevol Communiceren Cursus" className="w-full max-w-md mx-auto" data-id="element-1140" />
          </div>
          <div className="text-lg text-gray-700 leading-relaxed space-y-5 text-left max-w-2xl mx-auto" data-id="element-1141">
            <p data-id="element-1142">
              De Liefdevol Communiceren cursus is een complete online cursus van{' '}
              <strong data-id="element-1143">7 modules</strong> waarin je stap voor stap leert
              communiceren in hondentaal.
            </p>
            <p data-id="element-1144">
              Geen dominantie. Geen straffen. Geen commando's herhalen tot je er
              moe van wordt.
            </p>
            <p data-id="element-1145">
              Alleen: <strong data-id="element-1146">de echte taal van je hond leren spreken.</strong>{' '}
              Zodat jullie eindelijk écht op één lijn zitten.
            </p>
            <p data-id="element-1147">
              Je hond hoeft niets nieuws te leren. Hij spreekt deze taal al zijn
              hele leven.{' '}
              <strong data-id="element-1148">
                Jij leert zijn taal spreken - en dan begrijpt hij je gewoon.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 9: SECOND CTA */}
      <section className="bg-[#e6f7f5] py-16 md:py-20 px-5" id="bestellen" data-id="element-1149">
        <div className="max-w-xl mx-auto text-center" data-id="element-1150">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm font-medium text-[#1a2b4a] mb-6 shadow-sm" data-id="element-1151">
            <ClockIcon className="w-4 h-4" data-id="element-1152" /> Tijdelijke kortingsactie - Niet
            permanent beschikbaar
          </div>
          <PriceBlock data-id="element-1153" />
          <CTAButton data-id="element-1154" />
          <TrustIcons data-id="element-1155" />
        </div>
      </section>

      {/* SECTION 10: WHAT'S INCLUDED */}
      <section className="py-16 md:py-20 px-5" data-id="element-1156">
        <div className="max-w-3xl mx-auto" data-id="element-1157">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-12" data-id="element-1158">
            Dit is alles wat je krijgt voor slechts €127
          </h2>

          {/* Module Timeline */}
          <div className="space-y-6 mb-16" data-id="element-1159">
            {[{
            step: 1,
            title: 'Tools voor het dagelijks leven',
            value: '€49,95',
            desc: 'Je leert de kalmerende signalen die honden gebruiken om spanning te verminderen - en hoe jij ze zelf kunt inzetten. Hiermee doorbreek je miscommunicatie voordat die escaleert.'
          }, {
            step: 2,
            title: 'Omgangsregels',
            value: '€37',
            desc: 'Wat jij misschien ziet als "brutaal" gedrag, is vaak gewoon je hond die beleefd probeert te zijn op zijn eigen manier. Je leert die subtiele signalen herkennen en respecteren.'
          }, {
            step: 3,
            title: 'Tafelmanieren',
            value: '€17',
            desc: 'Leer hoe je impulscontrole opbouwt op een positieve manier - zodat opspringen bij bezoek en eten van tafel pakken tot het verleden behoren.'
          }, {
            step: 4,
            title: 'Jouw Reactie',
            value: '€24,95',
            desc: 'Wetenschappelijk bewezen: honden synchroniseren hun stressniveau met dat van hun baasje. In deze module leer je hoe je zelf rustiger wordt - en hoe dat direct effect heeft op je hond.'
          }, {
            step: 5,
            title: 'Het Handsignaal',
            value: '€22',
            desc: 'Honden begrijpen handgebaren van nature beter dan woorden. Leer hoe je grenzen stelt zonder te schreeuwen - met één rustig gebaar.'
          }, {
            step: 6,
            title: 'Het Attentiesignaal',
            value: '€22',
            desc: 'De allerbelangrijkste vaardigheid: eerst de aandacht van je hond pakken, dán communiceren. Dit voorkomt uitvallen en geeft je hond de zekerheid dat jij er voor hem bent.'
          }, {
            step: 7,
            title: 'Stressreductie',
            value: '€22',
            desc: 'Liefdevol communiceren verlaagt letterlijk het stressniveau van je hond. Leer hoe snuffelen, samen ontspannen en contact maken de hormoonbalans van je hond herstelt.'
          }].map((mod, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="flex gap-5 items-start" data-id="element-1160">
                <div className="w-12 h-12 rounded-full bg-[#5ce0d2] text-white flex items-center justify-center font-black text-lg flex-shrink-0 mt-1" data-id="element-1161">
                  {mod.step}
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex-1" data-id="element-1162">
                  <div className="flex items-start justify-between mb-2" data-id="element-1163">
                    <h4 className="font-bold text-[#1a2b4a] text-lg" data-id="element-1164">
                      Module {mod.step}: {mod.title}
                    </h4>
                    <span className="text-xs bg-[#e6f7f5] text-[#1a2b4a] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ml-3" data-id="element-1165">
                      Waarde: {mod.value}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1166">{mod.desc}</p>
                </div>
              </motion.div>)}
          </div>

          {/* Bonuses */}
          <h3 className="text-2xl font-bold text-[#1a2b4a] text-center mb-8" data-id="element-1167">
            En daarnaast ontvang je ook nog:
          </h3>
          <div className="grid md:grid-cols-3 gap-5 mb-12" data-id="element-1168">
            {[{
            icon: '🎁',
            title: 'Masterclass "Honden Geschiedenis"',
            desc: 'Begrijp waar je hond vandaan komt - en waarom hij doet wat hij doet.',
            value: '€7'
          }, {
            icon: '🎁',
            title: '8 Printbare Stappenplannen',
            desc: 'Voor elke communicatievaardigheid een overzichtelijk, printbaar stappenplan. Hang ze op de koelkast of neem ze mee op wandeling.',
            value: '€44'
          }, {
            icon: '🎁',
            title: 'Levenslange, onbeperkte toegang',
            desc: 'De cursus verdwijnt nooit. Kijk altijd terug, herhaal, en ontdek nieuwe dingen naarmate je hond groeit.',
            value: 'Onbetaalbaar'
          }].map((bonus, i) => <div key={i} className="bg-[#faf5eb] rounded-xl p-5 border border-gray-100" data-id="element-1169">
                <p className="text-2xl mb-3" data-id="element-1170">{bonus.icon}</p>
                <h4 className="font-bold text-[#1a2b4a] mb-2" data-id="element-1171">{bonus.title}</h4>
                <p className="text-sm text-gray-600 mb-3" data-id="element-1172">{bonus.desc}</p>
                <span className="text-xs bg-white text-[#1a2b4a] font-bold px-2.5 py-1 rounded-full" data-id="element-1173">
                  Waarde: {bonus.value}
                </span>
              </div>)}
          </div>

          {/* Value Summary */}
          <div className="bg-[#e6f7f5] rounded-2xl p-8 text-center border border-[#5ce0d2]/20" data-id="element-1174">
            <p className="text-gray-600 text-lg line-through mb-1" data-id="element-1175">
              Totale waarde: €223,90
            </p>
            <p className="text-3xl font-black text-[#1a2b4a]" data-id="element-1176">
              Jouw investering vandaag: €127 eenmalig
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 11: THIRD CTA */}
      <section className="bg-[#e6f7f5] py-16 md:py-20 px-5" data-id="element-1177">
        <div className="max-w-xl mx-auto text-center" data-id="element-1178">
          <PriceBlock data-id="element-1179" />
          <CTAButton data-id="element-1180" />
          <TrustIcons data-id="element-1181" />
        </div>
      </section>

      {/* SECTION 12: MORE TESTIMONIALS */}
      <section className="py-16 md:py-20 px-5" data-id="element-1182">
        <div className="max-w-6xl mx-auto" data-id="element-1183">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-12" data-id="element-1184">
            Meer dan 15.000 honden en baasjes gingen je al voor
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-1185">
            <TestimonialCard quote="Ik ben heel gelukkig met de online cursus. Je leert je hond te begrijpen en leert anders naar je hond te kijken op een hele fijne manier. Voor mens en hond!" name="Uschi Haase" data-id="element-1186" />
            <TestimonialCard quote="Fijne info en brengt rust in de opvoeding." name="Hellen Kleijberg, baasje van Mika en Moos" data-id="element-1187" />
            <TestimonialCard quote="Heel leerzaam om je hond te begrijpen. Raad het elke hondenbezitter aan!" name="C. en T. Swagerman" data-id="element-1188" />
            <TestimonialCard quote="Mijn hond Floris en ik vinden de oefeningen leuk. En Floris is een stuk rustiger geworden sinds wij regelmatig oefenen." name="Anneke Petersen, baasje van Floris" data-id="element-1189" />
            <TestimonialCard quote="Wij hebben een zeer overprikkelde hond en met de cursus liefdevol communiceren kregen we al waardevolle tips om de rust in huis te laten terugkeren." name="Cursist Liefdevol Communiceren" data-id="element-1190" />
            <TestimonialCard quote="Ik heb eindelijk het idee dat ik niet gek ben hoe ik mijn hond wil opvoeden. Ik wist alleen niet goed hoe. Gelukkig weet ik meer nu." name="Cursist Liefdevol Communiceren" data-id="element-1191" />
          </div>
          <div className="flex items-center justify-center gap-2 text-gray-500" data-id="element-1192">
            <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-1193" />
            <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-1194" />
            <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-1195" />
            <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-1196" />
            <span className="ml-2 font-medium" data-id="element-1197">
              4,3 / 5 op Trustpilot · 1054+ beoordelingen
            </span>
          </div>
        </div>
      </section>

      {/* SECTION 13: ABOUT AMEE */}
      <section className="bg-[#faf5eb] py-16 md:py-20 px-5" data-id="element-1198">
        <div className="max-w-5xl mx-auto" data-id="element-1199">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-12" data-id="element-1200">
            Over Amee van Stressless Dogs
          </h2>
          <div className="grid md:grid-cols-12 gap-10 items-start" data-id="element-1201">
            <div className="md:col-span-5" data-id="element-1202">
              <img src="https://cdn.magicpatterns.com/uploads/vXmyJc5cCVvbLnBaXGCixn/682f298cbb713_AmeemetSophietje1.png" alt="Amee de Bruijn" className="w-full rounded-2xl shadow-lg" data-id="element-1203" />
            </div>
            <div className="md:col-span-7" data-id="element-1204">
              <blockquote className="text-xl italic text-[#5ce0d2] mb-6 leading-relaxed" data-id="element-1205">
                "Goed en liefdevol communiceren kun je leren - en het is de
                sleutel tot een leuk en probleemloos leven samen met je hond."
              </blockquote>
              <div className="text-gray-700 leading-relaxed space-y-4" data-id="element-1206">
                <p data-id="element-1207">
                  Ik ben een gediplomeerd hondengedragsconsultant en Natural Dog
                  Trainer (niveau 5), opgeleid bij Calming Signs in Koningslust.
                  Ik werk uitsluitend met methodes die vrij zijn van dwang,
                  pijn, angst en straf.
                </p>
                <p data-id="element-1208">
                  In mijn werk staan altijd vier thema's centraal: vertrouwen,
                  veiligheid, wederzijds respect en communicatie.
                </p>
                <p data-id="element-1209">
                  Inmiddels heb ik meer dan 15.000 honden en hun baasjes mogen
                  helpen. Niet door ze te vertellen wat ze fout doen - maar door
                  ze te laten zien hoe ze in de taal van hun hond kunnen
                  communiceren.
                </p>
                <p className="font-bold text-[#1a2b4a]" data-id="element-1210">
                  Jij bent al een goed baasje. Je hebt alleen de juiste taal nog
                  niet geleerd.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-500" data-id="element-1211">
                <span data-id="element-1212">🎓 Gediplomeerd Gedragsconsultant</span>
                <span data-id="element-1213">🐾 Natural Dog Trainer Niveau 5</span>
                <span data-id="element-1214">👥 15.000+ geholpen</span>
                <span data-id="element-1215">⭐ 4,3 Trustpilot</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 14: IS THIS FOR YOU? */}
      <section className="py-16 md:py-20 px-5" data-id="element-1216">
        <div className="max-w-4xl mx-auto" data-id="element-1217">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-10" data-id="element-1218">
            Is Liefdevol Communiceren iets voor jou?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-1219">
            <div className="bg-red-50 rounded-2xl p-6 border border-red-100" data-id="element-1220">
              <h3 className="font-bold text-[#1a2b4a] text-lg mb-4" data-id="element-1221">
                Dit is NIET voor jou als je…
              </h3>
              <ul className="space-y-3" data-id="element-1222">
                {['Vindt dat een hond "gewoon moet gehoorzamen" zonder uitleg', 'Liever straft dan begrijpt', 'Geen tijd wilt investeren in een echte verbinding'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1223">
                    <XIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1224" />
                    <span className="text-gray-700" data-id="element-1225">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-green-50 rounded-2xl p-6 border border-green-100" data-id="element-1226">
              <h3 className="font-bold text-[#1a2b4a] text-lg mb-4" data-id="element-1227">
                Dit is WEL voor jou als je…
              </h3>
              <ul className="space-y-3" data-id="element-1228">
                {['Het beste met je hond voor hebt', 'Klaar bent met methoden die niet werken', 'Wilt samenwerken met je hond in plaats van commanderen', 'Die diepe, warme band wilt die je altijd al voor ogen had', 'Openstaat voor een nieuwe manier van kijken'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1229">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1230" />
                    <span className="text-gray-700" data-id="element-1231">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-600 italic max-w-2xl mx-auto" data-id="element-1232">
            "Ik had al veel geprobeerd en dacht dat ik al veel wist. Maar dit
            was een echte eyeopener. Je merkt nu pas hoeveel je uitgaat van
            praten in plaats van lichaamstaal." - Cursist met 12 jaar
            hondenervaring
          </p>
        </div>
      </section>

      {/* SECTION 15: GUARANTEE */}
      <section className="bg-[#faf5eb] py-16 md:py-20 px-5" data-id="element-1233">
        <div className="max-w-xl mx-auto text-center" data-id="element-1234">
          <div className="w-24 h-24 mx-auto mb-6 bg-[#5ce0d2] rounded-full flex items-center justify-center" data-id="element-1235">
            <ShieldIcon className="w-12 h-12 text-white" data-id="element-1236" />
          </div>
          <h2 className="text-3xl font-bold text-[#1a2b4a] mb-6" data-id="element-1237">
            Probeer het 30 dagen volledig risicovrij
          </h2>
          <div className="text-lg text-gray-700 leading-relaxed space-y-4" data-id="element-1238">
            <p data-id="element-1239">
              Ik ben zo overtuigd van wat de Hondentaal Methode voor jou en je
              hond kan betekenen, dat ik je een volledige garantie geef.
            </p>
            <p data-id="element-1240">
              Volg de cursus. Pas de communicatievaardigheden toe. En als je na
              30 dagen niet het gevoel hebt dat je je hond beter begrijpt en dat
              jullie band sterker is geworden - dan krijg je je geld volledig
              terug.
            </p>
            <p className="font-bold text-[#1a2b4a]" data-id="element-1241">
              Geen vragen gesteld. Want ik wil alleen tevreden baasjes en
              gelukkige honden.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 16: FINAL CTA */}
      <section className="bg-[#e6f7f5] py-16 md:py-24 px-5" data-id="element-1242">
        <div className="max-w-2xl mx-auto text-center" data-id="element-1243">
          <h2 className="text-3xl md:text-4xl font-black text-[#1a2b4a] mb-6 leading-tight" data-id="element-1244">
            Jouw hond praat de hele dag tegen je.
            <br data-id="element-1245" />
            Het is tijd om hem te antwoorden.
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed" data-id="element-1246">
            Elke dag zijn er momenten van contact, van communicatie, van
            verbinding. Maar ook: momenten van frustratie en miscommunicatie.
            Die momenten hoeven er niet te zijn. Niet omdat je hond anders moet
            worden. Maar omdat jij zijn taal leert spreken.
          </p>
          <p className="text-gray-500 mb-8" data-id="element-1247">
            Meer dan 15.000 baasjes gingen je al voor.
          </p>
          <PriceBlock data-id="element-1248" />
          <CTAButton className="text-xl py-5" data-id="element-1249" />
          <TrustIcons data-id="element-1250" />
          <p className="text-sm italic text-gray-500 mt-8" data-id="element-1251">
            "We garanderen een blije baas en een blije hond - anders storten we
            liever het geld terug." - Amee de Bruijn, Stressless Dogs
          </p>
        </div>
      </section>

      {/* SECTION 17: FAQ */}
      <section className="py-16 md:py-20 px-5" data-id="element-1252">
        <div className="max-w-3xl mx-auto" data-id="element-1253">
          <h2 className="text-3xl font-bold text-[#1a2b4a] text-center mb-10" data-id="element-1254">
            Veelgestelde vragen
          </h2>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden px-6" data-id="element-1255">
            <FAQItem q="Werkt dit ook als mijn hond al jarenlang hetzelfde gedrag vertoont?" a="Ja. De Hondentaal Methode gaat niet over het 'afleren' van gedrag - het gaat over het verbeteren van jullie communicatie. Honden zijn van nature sociale wezens die willen samenwerken. Ze wachten eigenlijk al op jou om hun taal te leren spreken." data-id="element-1256" />
            <FAQItem q="Ik heb al cursussen gevolgd en trainers gehad. Wat maakt dit anders?" a="De meeste cursussen richten zich op het gedrag van de hond: wat hij moet doen en laten. Liefdevol Communiceren richt zich op iets fundamentelers: de taal die jullie gebruiken om met elkaar te communiceren. Dat is de basis van alles. Zonder die basis werkt geen enkele techniek duurzaam." data-id="element-1257" />
            <FAQItem q="Mijn hond is erg angstig of overprikkeld. Is dit dan ook geschikt?" a="Juist dan is deze cursus waardevol. Angstige en overprikkelde honden reageren het sterkst op de manier waarop jij communiceert. Als jij leert hun signalen te lezen en in hun taal te antwoorden, neemt de stress bij veel honden snel merkbaar af." data-id="element-1258" />
            <FAQItem q="Ik heb al meer dan 10 jaar honden. Is dit dan nog iets nieuws voor mij?" a='Veel van onze meest enthousiaste cursisten zijn ervaren hondeneigenaren. Zoals één van hen schreef: "Na 12 jaar een Golden Retriever gehad te hebben, was het toch nog erg leerzaam. Je merkt nu pas hoeveel je uitgaat van praten in plaats van lichaamstaal."' data-id="element-1259" />
            <FAQItem q="Hoe lang heb ik toegang tot de cursus?" a="Levenslang en onbeperkt. Je kunt altijd terugkijken, herhalen, en de cursus opnieuw doorlopen als je een nieuwe hond krijgt of als je situatie verandert." data-id="element-1260" />
            <FAQItem q="Wanneer zie ik resultaat?" a="Veel cursisten zien al resultaat tijdens of direct na de eerste module. Dat komt omdat de Hondentaal Methode geen trucjes zijn die je moet inslijpen - het zijn communicatieprincipes die je hond van nature al begrijpt." data-id="element-1261" />
            <FAQItem q="Is de cursus ook geschikt voor een puppy of een senior hond?" a="Absoluut. Bij een puppy help je problemen voorkomen voordat ze ontstaan. Bij een oudere hond verbeter je een relatie die al jaren bestaat - en dat is nooit te laat." data-id="element-1262" />
            <FAQItem q="Kan ik ook betalen in termijnen?" a="Neem contact op via de website als je vragen hebt over betaalmogelijkheden. We denken graag met je mee." data-id="element-1263" />
          </div>
        </div>
      </section>

      {/* SECTION 18: FOOTER CTA BAR */}
      <div className="bg-[#1a2b4a] py-6 px-5" data-id="element-1264">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-center gap-4 text-center" data-id="element-1265">
          <p className="text-white font-medium" data-id="element-1266">
            Klaar om de taal van je hond te leren spreken?
          </p>
          <a href={CHECKOUT_URL} className="bg-[#ffb800] hover:bg-[#e6a600] text-[#1a2b4a] font-bold py-3 px-8 rounded-xl transition-colors shadow-md whitespace-nowrap" data-id="element-1267">
            Ja, ik wil beginnen voor €127 →
          </a>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="bg-[#111827] text-gray-400 py-12 text-xs leading-relaxed text-center px-4" data-id="element-1268">
        <div className="max-w-5xl mx-auto space-y-6" data-id="element-1269">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6YKzftRpGkz4jKTtn8FQxB/Logo_Stressless_Dogs_%288%29_%284%29.png" alt="Stressless Dogs" className="h-8 mx-auto mb-4 opacity-60" data-id="element-1270" />
          <p data-id="element-1271">
            © Copyright Stressless Dogs | stresslessdogs.nl | Alle rechten
            voorbehouden
          </p>
          <p className="font-bold text-white" data-id="element-1272">
            This site is not a part of the Facebook website or Facebook Inc.
            Additionally, This site is NOT endorsed by Facebook in any way.
            FACEBOOK is a trademark of FACEBOOK, Inc.
          </p>
          <p data-id="element-1273">
            vragen kun je altijd stellen door te mailen naar{' '}
            <a href="mailto:amee@stresslessdogs.nl" className="text-[#5ce0d2] hover:underline" data-id="element-1274">
              amee@stresslessdogs.nl
            </a>
          </p>
        </div>
      </footer>
    </div>;
}